// Universal search modal - cmd-K. Matters and clients come from the two
// real list endpoints; there is no server-side document search, so there
// is no documents group. The platform's navigation actions are static and
// mirror the g-chord shortcuts registered in App.tsx; module tiles join
// them from the screens registry.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEP } from "../chrome/SepList";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { useScreens } from "../chrome/screens";
import { initialsOf } from "../../api/auth";
import { OTHER_AREA, practiceAreaById } from "../../data/practice-areas";
import { clientNameLabel, stageLabel } from "../../lib/caseLabels";
import { useGlobalSearch, SEARCH_MIN_QUERY, SEARCH_DEBOUNCE_MS } from "../../hooks/useGlobalSearch";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface NavAction {
  label: string;
  to: string;
  chord?: string;
  adminOnly?: true;
}

const NAV_ACTIONS: NavAction[] = [
  { label: "Command Center", to: "/" },
  { label: "Cases", to: "/cases", chord: "g m" },
  { label: "Clients", to: "/clients", chord: "g c" },
  { label: "Calendar", to: "/calendar" },
  { label: "Filings", to: "/filings" },
  // The unmatched queue is the admin's (the server refuses everyone else).
  { label: "Inbox", to: "/inbox", adminOnly: true },
  { label: "Team", to: "/team", chord: "g t" },
  { label: "Profile", to: "/profile" },
  { label: "Integrations", to: "/settings/integrations" },
];

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      // Focus goes back to whatever opened the palette, as a dialog's does.
      const invoker = document.activeElement as HTMLElement | null;
      const id = setTimeout(() => inputRef.current?.focus(), 30);
      return () => {
        clearTimeout(id);
        // Blur first: a focused field or panel would still take Enter
        // during the fade.
        inputRef.current?.blur();
        panelRef.current?.blur();
        invoker?.focus();
      };
    }
    setQ("");
    setDebouncedQ("");
    setActive(0);
    return undefined;
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  const { data, isError, isFetching } = useGlobalSearch(open ? debouncedQ : "");
  const matters = data?.matters ?? [];
  const clients = data?.clients ?? [];

  const trimmed = q.trim();
  const searching = trimmed.length >= SEARCH_MIN_QUERY;
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const moduleActions: NavAction[] = useScreens().flatMap((s) =>
    (s.tiles ?? []).map((t) => ({ label: t.label, to: t.to, adminOnly: t.adminOnly })),
  );
  const navActions = [...NAV_ACTIONS, ...moduleActions].filter((a) => isAdmin || !a.adminOnly);
  const navMatches = trimmed
    ? navActions.filter((a) => a.label.toLowerCase().includes(trimmed.toLowerCase()))
    : navActions;
  const total = matters.length + clients.length + navMatches.length;

  // One flat list in display order, so the arrows walk what the eye sees.
  const rowTargets = [
    ...clients.map((c) => `/client/${c._id}`),
    ...matters.map(({ matter: m }) => `/matter/${m.eID}`),
    ...navMatches.map((a) => a.to),
  ];
  const activeIdx = Math.min(active, rowTargets.length - 1);

  useEffect(() => {
    setActive(0);
  }, [q, data]);

  useEffect(() => {
    resultsRef.current
      ?.querySelector(".v2-search-row.active")
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const run = (a: NavAction) => go(a.to);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // Consumed here: left to bubble, the shell would see the palette already
      // closed (React commits between listeners) and close the drawer too, and
      // a dialog under the palette would close on the same key.
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rowTargets.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Tab") {
      // A modal keeps focus; the field is its only stop.
      e.preventDefault();
      inputRef.current?.focus();
    } else if (e.key === "Enter") {
      // A query's results lag the typing by the debounce; until they land
      // the only rows are nav actions, and Enter would open a screen the
      // user never aimed at. IME composition commits are not Enter either,
      // nor is a held key. Cancelling the keydown also stops the keypress
      // that would press the button focus goes back to and reopen this.
      if (e.nativeEvent.isComposing || e.repeat) return;
      e.preventDefault();
      if (searching && !data) return;
      const to = rowTargets[activeIdx];
      if (to) go(to);
    }
  };

  return (
    <div
      className={"v2-search-backdrop" + (open ? " open" : "")}
      aria-hidden={!open}
      onClick={onClose}
    >
      {/* Focusable, and it reads the keys: a click on its dead space lands
          here, not on body, so an Escape pressed in the palette stays its own. */}
      <div
        ref={panelRef}
        className="v2-search"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="v2-search-input-row">
          <span className="ic">{">"}</span>
          <input
            ref={inputRef}
            placeholder="Search clients and matters, or jump to a screen..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="esc">esc</span>
        </div>
        <div className="v2-search-results" ref={resultsRef}>
          {searching && isError && (
            <div className="v2-search-empty">
              Could not reach the server - search is unavailable.
            </div>
          )}
          {trimmed.length > 0 && trimmed.length < SEARCH_MIN_QUERY && (
            <div className="v2-search-empty">
              Type at least {SEARCH_MIN_QUERY} characters to search.
            </div>
          )}
          {searching && isFetching && !data && (
            <div style={{ padding: "8px 16px" }}>
              <SkeletonGroup label="Searching">
                <Skeleton width="60%" height={14} />
                <Skeleton width="40%" height={14} />
              </SkeletonGroup>
            </div>
          )}
          {searching && !isError && !isFetching && total === 0 && (
            <div className="v2-search-empty">No matches for "{q}"</div>
          )}

          {clients.length > 0 && (
            <>
              <div className="v2-search-group-head">
                Clients{SEP}
                {clients.length}
              </div>
              {clients.map((c, i) => {
                const name = `${c.firstName} ${c.lastName}`;
                return (
                  <div
                    key={c._id}
                    className={"v2-search-row" + (i === activeIdx ? " active" : "")}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(`/client/${c._id}`)}
                  >
                    <div className="av">{initialsOf(name)}</div>
                    <div className="label">
                      <div className="name">{name}</div>
                      <div className="meta">
                        {c.mattersCount} matter{c.mattersCount === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {matters.length > 0 && (
            <>
              <div className="v2-search-group-head">
                Matters{SEP}
                {matters.length}
              </div>
              {matters.map(({ matter: m }, i) => {
                // A matter outside the registry is an Other matter, as /cases groups it.
                const area = practiceAreaById(m.practiceArea) ?? OTHER_AREA;
                const idx = clients.length + i;
                return (
                  <div
                    key={m.eID}
                    className={"v2-search-row" + (idx === activeIdx ? " active" : "")}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => go(`/matter/${m.eID}`)}
                  >
                    <div className="av" style={{ background: area.accentStrong }}>
                      {area.shortLabel.slice(0, 2)}
                    </div>
                    <div className="label">
                      <div className="name">
                        {clientNameLabel(m)}{" "}
                        <span className="eid" style={{ marginLeft: 6 }}>
                          {m.eID}
                        </span>
                      </div>
                      <div className="meta">
                        {area.label}
                        {m.stage ? SEP + stageLabel(m.stage) : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {navMatches.length > 0 && (
            <>
              <div className="v2-search-group-head">
                Go to{SEP}
                {navMatches.length}
              </div>
              {navMatches.map((a, i) => {
                const idx = clients.length + matters.length + i;
                return (
                  <div
                    key={a.label}
                    className={"v2-search-row" + (idx === activeIdx ? " active" : "")}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => run(a)}
                  >
                    <div
                      className="av"
                      style={{ background: "var(--line)", color: "var(--muted)" }}
                    >
                      {">"}
                    </div>
                    <div className="label">
                      <div className="name">{a.label}</div>
                    </div>
                    {a.chord && <span className="esc">{a.chord}</span>}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
